import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {MouseEvent, ReactElement, UIEvent} from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {Icon, LoadingSpinner} from 'pepsico-ds'
import {SidePanel} from '@/components/GDS/organisms/SidePanel'
import {useIsDarkMode} from '@/hooks/useThemeMode'
import {normalizeMarkdownContent} from '@/components/Docs/docsUtils'
import {fetchDocsArticleBySlug} from '@/services/cms/docsCmsService'
import type {DocsArticleContent} from '@/services/cms/docsCmsService'
import {DOCS_HOME_PATH} from '@/routes/pageDocs'
import {getPageDocsMarkdownComponents, PAGE_DOCS_FONT_FAMILY} from './pageDocsMarkdown'
import PageDocsVideoModal from './PageDocsVideoModal'
import PageDocsVideoThumbnail from './PageDocsVideoThumbnail'
import {splitDocsBodyForVideo} from './splitDocsBodyForVideo'

export const DOCS_PANEL_WIDTH = '480px'

/** Scroll distance (px) after which the go to top button appears. */
const SCROLL_TOP_THRESHOLD = 160

interface PageDocsSidePanelProps {
  isOpen: boolean
  onClose: () => void
  docsSlug: string | null
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'error'

const PageDocsSidePanel = ({isOpen, onClose, docsSlug}: PageDocsSidePanelProps): ReactElement | null => {
  const isDark = useIsDarkMode()
  const [docs, setDocs] = useState<DocsArticleContent | null>(null)
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const [searchQuery, setSearchQuery] = useState('')
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsVideoOpen(false)
    if (!isOpen) return
    if (docsSlug === null) {
      setDocs(null)
      setLoadState('error')
      return
    }

    let isCancelled = false
    setLoadState('loading')
    setSearchQuery('')
    setShowScrollTop(false)

    fetchDocsArticleBySlug(docsSlug)
      .then(result => {
        if (isCancelled) return
        setDocs(result)
        setLoadState(result === null ? 'error' : 'loaded')
      })
      .catch(() => {
        if (isCancelled) return
        setDocs(null)
        setLoadState('error')
      })

    return () => {
      isCancelled = true
    }
  }, [isOpen, docsSlug])

  const query = searchQuery.trim()
  const normalizedBody = useMemo(() => (docs ? normalizeMarkdownContent(docs.body) : ''), [docs])
  const markdownComponents = useMemo(() => getPageDocsMarkdownComponents(isDark, query), [isDark, query])
  // The video lives in its own CMS field, so the body is split to place it after the intro.
  const videoParts = useMemo(
    () => (docs?.video ? splitDocsBodyForVideo(normalizedBody) : null),
    [docs?.video, normalizedBody],
  )

  // Bring the first highlighted match into view once the markdown has been re-rendered for the current query.
  useEffect(() => {
    if (!query) return
    contentRef.current?.querySelector('mark')?.scrollIntoView?.({block: 'center', behavior: 'smooth'})
  }, [query, normalizedBody, loadState])

  const handleContentScroll = (event: UIEvent<HTMLDivElement>): void => {
    setShowScrollTop(event.currentTarget.scrollTop > SCROLL_TOP_THRESHOLD)
  }

  const scrollToTop = (): void => {
    contentRef.current?.scrollTo?.({top: 0, behavior: 'smooth'})
  }

  // Article bookmarks (`#heading-id`) scroll inside the slider instead of changing the page URL.
  const handleContentClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (!(event.target instanceof Element)) return
    const href = event.target.closest('a')?.getAttribute('href')
    if (href === null || href === undefined || !href.startsWith('#') || href.length < 2) return

    event.preventDefault()
    const id = href.slice(1)
    const target = Array.from(contentRef.current?.querySelectorAll('[id]') ?? []).find(element => element.id === id)
    target?.scrollIntoView?.({block: 'start', behavior: 'smooth'})
  }

  const mutedText = isDark ? 'text-[var(--color-text-secondary)]' : 'text-slate-500'
  const closeVideo = useCallback((): void => setIsVideoOpen(false), [])

  return (
    <SidePanel
      showSidePanel={isOpen}
      onCloseSidePanel={onClose}
      title={docs?.title ?? 'Documentation'}
      type="vertical"
      horizontalSidepanelWidth={DOCS_PANEL_WIDTH}
      className="docs-side-panel"
    >
      <div data-testid="page-docs-panel" className="flex min-h-0 flex-1 flex-col gap-3">
        <div
          className={`flex items-center gap-2 border-b pb-4 ${isDark ? 'border-[var(--color-border-default)]' : 'border-slate-200'}`}
        >
          <input
            type="search"
            value={searchQuery}
            onChange={(event): void => setSearchQuery(event.target.value)}
            placeholder="Search in this doc..."
            aria-label="Search in this document"
            data-testid="page-docs-search"
            className={`min-w-0 flex-1 rounded-md border px-3 py-2 text-[14px] outline-none ${
              isDark
                ? 'border-[var(--color-border-default)] bg-[var(--color-background-layer-01)] text-[var(--color-text-primary)] focus:border-blue-400'
                : 'border-slate-300 bg-white text-slate-900 focus:border-blue-500'
            }`}
          />
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={contentRef}
            className="min-h-0 flex-1 overflow-y-auto pr-1"
            data-testid="page-docs-content"
            onScroll={handleContentScroll}
            onClick={handleContentClick}
          >
            {loadState === 'loading' && (
              <div className="flex min-h-[40vh] items-center justify-center">
                <LoadingSpinner size={{width: '32px', height: '32px'}} />
              </div>
            )}
            {loadState === 'error' && (
              <div className={`text-sm leading-7 ${mutedText}`} data-testid="page-docs-error">
                {docsSlug === null
                  ? 'No documentation is configured for this page.'
                  : 'Unable to load the documentation for this page.'}{' '}
                <a
                  href={DOCS_HOME_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-4"
                >
                  Open all docs
                </a>
              </div>
            )}
            {loadState === 'loaded' && (
              <>
                {docs?.video && videoParts !== null ? (
                  <>
                    {videoParts.intro !== '' && (
                      <div
                        className="docs-markdown [&>p:first-child]:text-[15px] [&>p:first-child]:leading-[1.65]"
                        style={{fontFamily: PAGE_DOCS_FONT_FAMILY}}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {videoParts.intro}
                        </ReactMarkdown>
                      </div>
                    )}
                    <div className={`${videoParts.intro !== '' ? 'mt-5' : ''} ${videoParts.rest === '' ? 'pb-6' : ''}`}>
                      <PageDocsVideoThumbnail
                        video={docs.video}
                        isDark={isDark}
                        onPlay={(): void => setIsVideoOpen(true)}
                      />
                    </div>
                    {videoParts.rest !== '' && (
                      <div className="docs-markdown pb-6" style={{fontFamily: PAGE_DOCS_FONT_FAMILY}}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                          {videoParts.rest}
                        </ReactMarkdown>
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className="docs-markdown pb-6 [&>p:first-child]:text-[15px] [&>p:first-child]:leading-[1.65]"
                    style={{fontFamily: PAGE_DOCS_FONT_FAMILY}}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {normalizedBody}
                    </ReactMarkdown>
                  </div>
                )}
              </>
            )}
          </div>
          {showScrollTop && (
            <button
              type="button"
              title="Back to top"
              aria-label="Scroll to top of the article"
              data-testid="page-docs-scroll-top"
              onClick={scrollToTop}
              className={`absolute right-4 bottom-3 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border shadow-md transition hover:brightness-95 ${
                isDark
                  ? 'border-[var(--color-border-default)] bg-[var(--color-background-layer-02)] text-[var(--color-text-primary)]'
                  : 'border-slate-300 bg-white text-slate-700'
              }`}
            >
              <Icon icon="arrow_upward" size="small" style={{color: 'currentColor'}} />
            </button>
          )}
        </div>
      </div>
      {isVideoOpen && docs?.video && <PageDocsVideoModal video={docs.video} onClose={closeVideo} />}
    </SidePanel>
  )
}

export default PageDocsSidePanel
